import {ApolloError, FetchResult, gql, MutationHookOptions, MutationTuple, useMutation} from '@apollo/client'
import {useAlert} from 'react-alert'
import {getGraphQLErrorMessage} from '~/util'
import {RequestSelfBuildUserOTP, RequestSelfBuildUserOTPVariables} from './__types__/RequestSelfBuildUserOTP'
import {CreateSelfBuildDirectory, CreateSelfBuildDirectoryVariables} from './__types__/CreateSelfBuildDirectory'
import {RequestStripeCustomerOTP, RequestStripeCustomerOTPVariables} from './__types__/RequestStripeCustomerOTP'
import {CreateStripeDirectory, CreateStripeDirectoryVariables} from './__types__/CreateStripeDirectory'
import {SetViewedSelfBuildWelcomePages} from './__types__/SetViewedSelfBuildWelcomePages'

const REQUEST_SELF_BUILD_USER_OTP = gql`
    mutation RequestSelfBuildUserOTP($email: String!, $firstName: String, $lastName: String) {
        requestSelfBuildUserOTP(email: $email, firstName: $firstName, lastName: $lastName)
    }
`

const CREATE_SELF_BUILD_DIRECTORY = gql`
    mutation CreateSelfBuildDirectory($organisationName: String!, $organisationToClone: String) {
        createSelfBuildDirectory(organisationName: $organisationName, organisationToClone: $organisationToClone)
    }
`

const REQUEST_STRIPE_CUSTOMER_OTP = gql`
    mutation RequestStripeCustomerOTP($customerId: String!) {
        requestStripeCustomerOTP(customerId: $customerId)
    }
`

const CREATE_STRIPE_DIRECTORY = gql`
    mutation CreateStripeDirectory($organisationName: String!, $customerId: String!, $organisationToClone: String) {
        createStripeDirectory(
            organisationName: $organisationName
            customerId: $customerId
            organisationToClone: $organisationToClone
        )
    }
`

const SET_VIEWED_SELF_BUILD_WELCOME_PAGES = gql`
    mutation SetViewedSelfBuildWelcomePages {
        setViewedSelfBuildWelcomePages
    }
`

export const useMutationRequestSelfBuildUserOtp = (
    options?: MutationHookOptions<RequestSelfBuildUserOTP, RequestSelfBuildUserOTPVariables>,
): [
    MutationTuple<RequestSelfBuildUserOTP, RequestSelfBuildUserOTPVariables>,
    (variables: {
        email: string
        firstName?: string
        lastName?: string
    }) => Promise<FetchResult<RequestSelfBuildUserOTP, Record<string, any>, Record<string, any>>>,
] => {
    const alert = useAlert()
    const mutation = useMutation<RequestSelfBuildUserOTP, RequestSelfBuildUserOTPVariables>(
        REQUEST_SELF_BUILD_USER_OTP,
        options,
    )
    const callMutation = async (variables: {email: string; firstName?: string; lastName?: string}) => {
        try {
            const result = await mutation[0]({variables})
            return result
        } catch (error) {
            alert.error(getGraphQLErrorMessage(error as ApolloError))
            throw error
        }
    }
    return [mutation, callMutation]
}

export const useMutationCreateSelfBuildDirectory = (
    options?: MutationHookOptions<CreateSelfBuildDirectory, CreateSelfBuildDirectoryVariables>,
): [
    MutationTuple<CreateSelfBuildDirectory, CreateSelfBuildDirectoryVariables>,
    (variables: {
        organisationName: string
        organisationToClone?: string
    }) => Promise<FetchResult<CreateSelfBuildDirectory, Record<string, any>, Record<string, any>>>,
] => {
    const alert = useAlert()
    const mutation = useMutation<CreateSelfBuildDirectory, CreateSelfBuildDirectoryVariables>(
        CREATE_SELF_BUILD_DIRECTORY,
        options,
    )
    const callMutation = async (variables: {organisationName: string; organisationToClone?: string}) => {
        try {
            const result = await mutation[0]({variables})
            return result
        } catch (error) {
            alert.error(getGraphQLErrorMessage(error as ApolloError))
            throw error
        }
    }
    return [mutation, callMutation]
}

export const useMutationRequestStripeCustomerOtp = (
    options?: MutationHookOptions<RequestStripeCustomerOTP, RequestStripeCustomerOTPVariables>,
): [
    MutationTuple<RequestStripeCustomerOTP, RequestStripeCustomerOTPVariables>,
    (variables: {
        customerId: string
    }) => Promise<FetchResult<RequestStripeCustomerOTP, Record<string, any>, Record<string, any>>>,
] => {
    const alert = useAlert()
    const mutation = useMutation<RequestStripeCustomerOTP, RequestStripeCustomerOTPVariables>(
        REQUEST_STRIPE_CUSTOMER_OTP,
        options,
    )
    const callMutation = async (variables: {customerId: string}) => {
        try {
            const result = await mutation[0]({variables})
            return result
        } catch (error) {
            alert.error(getGraphQLErrorMessage(error as ApolloError))
            throw error
        }
    }
    return [mutation, callMutation]
}

export const useMutationCreateStripeDirectory = (
    options?: MutationHookOptions<CreateStripeDirectory, CreateStripeDirectoryVariables>,
): [
    MutationTuple<CreateStripeDirectory, CreateStripeDirectoryVariables>,
    (variables: {
        organisationName: string
        customerId: string
        organisationToClone?: string
    }) => Promise<FetchResult<CreateStripeDirectory, Record<string, any>, Record<string, any>>>,
] => {
    const alert = useAlert()
    const mutation = useMutation<CreateStripeDirectory, CreateStripeDirectoryVariables>(
        CREATE_STRIPE_DIRECTORY,
        options,
    )
    const callMutation = async (variables: {
        organisationName: string
        customerId: string
        organisationToClone?: string
    }) => {
        try {
            const result = await mutation[0]({variables})
            return result
        } catch (error) {
            alert.error(getGraphQLErrorMessage(error as ApolloError))
            throw error
        }
    }
    return [mutation, callMutation]
}

export const useMutationSetViewedSelfBuildWelcomePages = (
    options?: MutationHookOptions<SetViewedSelfBuildWelcomePages>,
): [
    MutationTuple<SetViewedSelfBuildWelcomePages, {}>,
    (variables: {}) => Promise<FetchResult<SetViewedSelfBuildWelcomePages, Record<string, any>, Record<string, any>>>,
] => {
    const alert = useAlert()
    const mutation = useMutation<SetViewedSelfBuildWelcomePages>(SET_VIEWED_SELF_BUILD_WELCOME_PAGES, options)
    const callMutation = async (variables: {}) => {
        try {
            const result = await mutation[0]({variables})
            return result
        } catch (error) {
            alert.error(getGraphQLErrorMessage(error as ApolloError))
            throw error
        }
    }
    return [mutation, callMutation]
}
